import {
  allLeavesOfType,
  childNodesOfType,
  leavesOfType,
} from 'components/utility/TreeHelpers'
import { LeafTypes, NavigableNodeTypes, NodeType } from 'shared/types/ingestion'
import { INode } from 'shared/types/tree'
import { makeContentServicePostRequest } from 'infrastructure/contentService'
import { KiddomStandardsResp, KiddomStandard } from 'components/standards/types'
import {
  SheetNode,
  SheetSkillsList,
  SheetSkill,
  ContentServiceSyncRequest,
  ImportedSheetData,
  SkillsListDifference,
} from './types'
import { directTreeAccessUrl } from 'infrastructure/api'

/**
 * Serialize ImportedSheetData for JSON export/download.
 * Currently a pass-through function since ImportedSheetData is already JSON-serializable.
 * This function exists for future extensibility if non-serializable types (e.g., Maps, Sets) are added.
 *
 * @param data - ImportedSheetData to serialize
 * @returns The data as-is (already JSON-serializable)
 */
export function serializeImportedSheetData(
  data: ImportedSheetData,
): ImportedSheetData {
  return data
}

/**
 * Check if an error is an authentication error
 */
export function isAuthError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false
  }
  const errorMessage = error.message.toLowerCase()
  return (
    errorMessage.includes('invalid authentication') ||
    errorMessage.includes('invalid_token') ||
    errorMessage.includes('token expired') ||
    errorMessage.includes('authentication credential') ||
    errorMessage.includes('unauthorized') ||
    errorMessage.includes('401')
  )
}

/**
 * Calculates summary statistics from a list of SheetNodes
 * @param nodes - Array of sheet nodes to analyze
 * @returns Summary object with counts of nodes, skills, and breakdowns by type
 */
export function calculateSheetNodesSummary(nodes: SheetNode[]) {
  // Total number of nodes
  const nodeCount = nodes.length

  // Count nodes with skills in a single loop
  let nodesWithSkills = 0
  const nodesWithSkillsByType: Record<string, number> = {}

  nodes.forEach((node) => {
    // Check if node has existing skills lists with non-empty skills
    const hasSkillsInLists = node.skillsLists.some(
      (list) => list.skills.length > 0,
    )
    // Check if node has new skills lists with non-empty skills
    const hasSkillsInNewLists = node.newSkillsLists.some(
      (list) => list.skills.length > 0,
    )

    // Count nodes with either existing or new skills (or both)
    const hasSkills = hasSkillsInLists || hasSkillsInNewLists
    if (hasSkills) {
      nodesWithSkills++

      // Count nodes with skills by type
      if (node.type) {
        if (!nodesWithSkillsByType[node.type]) {
          nodesWithSkillsByType[node.type] = 0
        }
        nodesWithSkillsByType[node.type] += 1
      }
    }
  })

  // Total count of all skills across all nodes (existing and new skills lists)
  const skillsCount = nodes.reduce(
    (sum, node) =>
      sum +
      node.skillsLists.reduce(
        (listSum, list) => listSum + list.skills.length,
        0,
      ) +
      node.newSkillsLists.reduce(
        (listSum, list) => listSum + list.skills.length,
        0,
      ),
    0,
  )

  return {
    nodeCount,
    nodesWithSkills,
    skillsCount,
    nodesWithSkillsByType,
  }
}

/**
 * Creates summary rows for sheet standards display (used by both import and export)
 */
export function createSheetSummaryRows(
  summary: ReturnType<typeof calculateSheetNodesSummary>,
) {
  const rows: Array<{ cells: (string | number)[] }> = [
    { cells: ['Total nodes with skills', summary.nodesWithSkills] },
  ]

  // Add nodes with skills by type
  const typesWithNodesContainingSkills = Object.keys(
    summary.nodesWithSkillsByType,
  ).sort((a, b) => a.localeCompare(b))
  typesWithNodesContainingSkills.forEach((type) => {
    const count = summary.nodesWithSkillsByType[type]
    if (count > 0) {
      rows.push({ cells: [`${type} nodes with skills`, count] })
    }
  })

  rows.push({ cells: ['Total standards', summary.skillsCount] })

  return rows
}

/**
 * Strips HTML tags from a string
 */
export function stripHTML(html: string) {
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = html
  return tempDiv.innerText
}

/**
 * Scans the tree and extracts all unique standard IDs from skills.
 * Does not interpret the IDs, just collects them.
 *
 * @param tree - The tree node to scan
 * @returns Set of unique standard IDs
 */
export function extractStandardIdsFromTree(tree: INode): Set<number> {
  const allUniqueStandardIDs = new Set<number>(
    allLeavesOfType(tree, LeafTypes.Skill)
      .map((s) => s.body.skillID)
      .filter((id): id is number => id !== undefined && id !== null),
  )
  return allUniqueStandardIDs
}

/**
 * Fetches Kiddom standards by their IDs from the content service API.
 *
 * @param standardIds - Set of standard IDs to fetch
 * @returns Promise resolving to a map of standard_id -> KiddomStandard
 */
export async function fetchStandardsByIds(
  standardIds: Set<number>,
): Promise<Record<number, KiddomStandard>> {
  if (standardIds.size === 0) {
    return {}
  }

  const standardIdsArray = Array.from(standardIds)
  const response = await makeContentServicePostRequest(
    'content/v1/standards/kiddom/by_ids',
    {
      IDs: standardIdsArray,
    },
  )

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(
      `Failed to fetch standards: HTTP ${response.status}: ${errorText}`,
    )
  }

  const data: KiddomStandardsResp = await response.json()

  // Convert array to map with key=standard_id and value=KiddomStandard
  const standardMap: Record<number, KiddomStandard> = {}
  if (data.data && Array.isArray(data.data)) {
    data.data.forEach((standard) => {
      standardMap[standard.standard_id] = standard
    })
  }

  return standardMap
}

/**
 * Converts tree structure to SheetNodes format.
 * Extracts nodes with their skills lists and skills.
 * Also builds parent ID map for hierarchy construction.
 *
 * @param tree - The tree node to convert
 * @param standardIdMap - Map of standard_id -> KiddomStandard
 * @returns Object with flatNodes array and nodeIdToParentIdMap
 */
export function extractSheetNodesFromTree(
  tree: INode,
  standardIdMap: Record<number, KiddomStandard>,
  treeId: string,
  versionId: string,
): {
  flatNodes: SheetNode[]
  nodeIdToParentIdMap: Map<string, string>
} {
  const result: SheetNode[] = []
  const nodeIdToParentIdMap = new Map<string, string>()

  function recursivelyExtractNodes(node: INode, parentId: string | null) {
    if (
      (node.title && NavigableNodeTypes.includes(node.type as NodeType)) ||
      node.type === NodeType.Question
    ) {
      const nodeText =
        node.type === NodeType.Question && node.description
          ? stripHTML(node.description).substring(0, 100)
          : node.title || ''

      // Get all child skills lists with their associated standards
      const existingStandardLists = childNodesOfType(node, NodeType.SkillsList)

      const skillsLists: SheetSkillsList[] = existingStandardLists.map(
        (skillsListNode) => {
          const skillIDs = leavesOfType(skillsListNode, LeafTypes.Skill)
            .map((s) => s.body.skillID)
            .filter((id): id is number => id !== undefined && id !== null)

          const skills: SheetSkill[] = skillIDs
            .map((id) => {
              const standard = standardIdMap[id]
              if (standard) {
                return {
                  skill_id: id,
                  name: standard.standard_name || '',
                }
              }
              return null
            })
            .filter((skill): skill is SheetSkill => skill !== null)

          return {
            nodeid: skillsListNode.id,
            title: skillsListNode.title || '',
            skills,
          }
        },
      )

      // Use tree_id and version from node if available, otherwise use parameters
      const effectiveTreeId = node.tree_id || treeId
      const effectiveVersionId = node.version || versionId

      // Check if this is a navigable node type for link generation
      const isNavigableType = NavigableNodeTypes.includes(node.type as NodeType)
      const isQuestion = node.type === NodeType.Question

      // Generate link for navigable types or questions, otherwise empty string
      let link: string = ''
      if (isNavigableType || isQuestion) {
        let nodeIdForLink: string
        // Special case for questions: use parent's nodeId for link
        if (isQuestion && parentId) {
          nodeIdForLink = parentId
        } else {
          nodeIdForLink = node.id
        }
        link = directTreeAccessUrl({
          treeId: effectiveTreeId,
          versionId: effectiveVersionId,
          nodeId: nodeIdForLink,
        })
      }

      const sheetNode: SheetNode = {
        node_id: node.id,
        type: node.type || '',
        title: nodeText,
        skillsLists,
        newSkillsLists: [],
        link,
      }

      result.push(sheetNode)

      // Track parent relationship
      if (parentId) {
        nodeIdToParentIdMap.set(node.id, parentId)
      }
    }

    node.nodes?.forEach((n) => {
      recursivelyExtractNodes(n, node.id)
    })
  }

  recursivelyExtractNodes(tree, null)

  return {
    flatNodes: result,
    nodeIdToParentIdMap,
  }
}

/**
 * Starts a flow to sync standards based on differences between sheet and tree.
 *
 * @param body - The request parameters
 * @param body.tree_id - The tree ID to sync standards to
 * @param body.version_id - The version ID of the tree
 * @param body.differences - List of ContentServiceSyncRequest objects
 *
 * @returns Promise<string> - The flow ID of the started flow
 */
export async function startSyncStandardsFlow(body: {
  tree_id: string
  version_id: string
  differences: ContentServiceSyncRequest[]
}): Promise<string> {
  const response = await makeContentServicePostRequest(
    'content/v1/standards/sync/job/start',
    {
      tree_id: body.tree_id,
      version_id: body.version_id,
      differences: body.differences,
    },
  )

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(
      `Sync flow start failed with status ${response.status}: ${errorText}`,
    )
  }

  const data = await response.json()
  if (!data.data?.flow_id) {
    throw new Error('No flow ID received from sync API')
  }

  return data.data.flow_id
}

/**
 * Formats standard IDs from an array of SkillsListStandard objects
 */
export function formatStandardIdsFromArray(
  skills: SkillsListDifference['tree_skills_list'],
): string {
  return skills
    .map((s) => s.standard_id)
    .sort((a, b) => a - b)
    .join(' ')
}

/**
 * Formats standard IDs from a Record of SkillsListStandard objects
 */
export function formatStandardIdsFromRecord(
  skills: SkillsListDifference['sheet_skills'],
): string {
  return Object.keys(skills)
    .map(Number)
    .sort((a, b) => a - b)
    .join(' ')
}
